/**
 * 606 MT5 AI TRADING - Main Page
 * Redesigned with better layout and organization
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  TrendingUp, LayoutDashboard, Briefcase, History, Newspaper, 
  Calendar, BarChart3, Bot, FlaskConical, Settings, Activity,
  Zap, Globe, Shield, GraduationCap
} from 'lucide-react';
import { TradingProvider } from '../context/TradingContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';

// Tab Components
import DashboardTab from './trading/DashboardTab';
import PositionsTab from './trading/PositionsTab';
import HistoryTab from './trading/HistoryTab';
import NewsTab from './trading/NewsTab';
import CalendarTab from './trading/CalendarTab';
import AnalyticsTab from './trading/AnalyticsTab';
import AILogTab from './trading/AILogTab';
import BacktestTab from './trading/BacktestTab';
import SettingsTab from './trading/SettingsTab';
import SchoolsTab from './trading/SchoolsTab';

const TABS = [
  { value: 'dashboard', labelAr: 'لوحة التحكم', labelEn: 'DASHBOARD', icon: LayoutDashboard, color: '#d4af37' },
  { value: 'schools', labelAr: 'المدارس التداولية', labelEn: 'SCHOOLS', icon: GraduationCap, color: '#d4af37' },
  { value: 'positions', labelAr: 'الصفقات المفتوحة', labelEn: 'POSITIONS', icon: Briefcase, color: '#22c55e' },
  { value: 'history', labelAr: 'سجل الصفقات', labelEn: 'HISTORY', icon: History, color: '#8b5cf6' },
  { value: 'news', labelAr: 'الأخبار', labelEn: 'NEWS', icon: Newspaper, color: '#f97316' },
  { value: 'calendar', labelAr: 'التقويم الاقتصادي', labelEn: 'CALENDAR', icon: Calendar, color: '#06b6d4' },
  { value: 'analytics', labelAr: 'التحليلات', labelEn: 'ANALYTICS', icon: BarChart3, color: '#ec4899' },
  { value: 'ai-log', labelAr: 'سجل الذكاء الاصطناعي', labelEn: 'AI LOG', icon: Bot, color: '#10b981' },
  { value: 'backtest', labelAr: 'الاختبار الخلفي', labelEn: 'BACKTEST', icon: FlaskConical, color: '#6366f1' },
  { value: 'settings', labelAr: 'الإعدادات', labelEn: 'SETTINGS', icon: Settings, color: '#71717A' },
];

const Meta5Trading = () => {
  const [params, setParams] = useSearchParams();
  const { language } = useLanguage();
  const activeTab = params.get('tab') || 'dashboard';

  const handleTabChange = (value) => {
    setParams({ tab: value });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardTab language={language} />;
      case 'schools': return <SchoolsTab language={language} />;
      case 'positions': return <PositionsTab language={language} />;
      case 'history': return <HistoryTab language={language} />;
      case 'news': return <NewsTab language={language} />;
      case 'calendar': return <CalendarTab language={language} />;
      case 'analytics': return <AnalyticsTab language={language} />;
      case 'ai-log': return <AILogTab language={language} />;
      case 'backtest': return <BacktestTab language={language} />;
      case 'settings': return <SettingsTab language={language} />;
      default: return <DashboardTab language={language} />;
    }
  };

  return (
    <DashboardLayout>
      <TradingProvider>
        <div className="min-h-screen pb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          {/* Hero Header */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative mb-8 p-6 bg-gradient-to-br from-[#d4af37]/10 via-transparent to-emerald-500/5 rounded-2xl border border-[#d4af37]/20 overflow-hidden"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-32 h-32 bg-[#d4af37] rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-emerald-500 rounded-full blur-3xl" />
            </div>
            
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              {/* Logo & Title */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#d4af37] to-[#c9a227] flex items-center justify-center shadow-lg shadow-[#d4af37]/30">
                  <TrendingUp className="h-8 w-8 text-black" />
                </div>
                <div>
                  <h1 dir="ltr" className="text-2xl md:text-3xl font-bold text-white tracking-wide text-left">
                    606 MT5 AI TRADING
                  </h1>
                  <p className="text-[#A1A1AA] text-sm mt-1">
                    {language === 'ar' 
                      ? 'بوت التداول الذكي المدعوم بالذكاء الاصطناعي' 
                      : 'AI-POWERED AUTOMATED TRADING BOT'}
                  </p>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-xl border border-white/10">
                  <Activity className="h-4 w-4 text-emerald-400" />
                  <span className="text-xs text-[#A1A1AA]">GPT-5.2</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-xl border border-white/10">
                  <Zap className="h-4 w-4 text-purple-400" />
                  <span className="text-xs text-[#A1A1AA]">CLAUDE 4.5</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-xl border border-white/10">
                  <Globe className="h-4 w-4 text-blue-400" />
                  <span className="text-xs text-[#A1A1AA]">4 {language === 'ar' ? 'أزواج' : 'PAIRS'}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-xl border border-white/10">
                  <Shield className="h-4 w-4 text-[#d4af37]" />
                  <span className="text-xs text-[#A1A1AA]">{language === 'ar' ? 'إدارة المخاطر' : 'RISK MANAGED'}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6"
          >
            <div className="flex flex-wrap gap-2">
              {TABS.map((tab, index) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.value;
                return (
                  <motion.button
                    key={tab.value}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => handleTabChange(tab.value)}
                    data-testid={`tab-${tab.value}`}
                    className={`group flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all duration-300 ${
                      isActive
                        ? 'bg-[#d4af37]/20 border-[#d4af37]/40 text-[#d4af37] shadow-lg shadow-[#d4af37]/10'
                        : 'bg-white/[0.02] border-white/10 text-[#71717A] hover:text-white hover:bg-white/[0.05] hover:border-white/20'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${
                      isActive ? 'bg-[#d4af37]/30' : 'bg-white/5 group-hover:bg-white/10'
                    }`}>
                      <Icon className="h-4 w-4" style={{ color: isActive ? '#d4af37' : tab.color }} />
                    </div>
                    <span className="text-sm font-medium whitespace-nowrap">
                      {language === 'ar' ? tab.labelAr : tab.labelEn}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </TradingProvider>
    </DashboardLayout>
  );
};

export default Meta5Trading;
